import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Emoji from './emoji';
import { useTranslation } from 'react-i18next';

// Airbnb-style search bar component
function Search({ destinations, jumbo }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    // Search state
    const [location, setLocation] = useState(null);
    const [locationInput, setLocationInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [date, setDate] = useState('');
    const [guests, setGuests] = useState(1);
    
    // UI state
    const [activeField, setActiveField] = useState(null);
    const searchRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setActiveField(null);
                setSuggestions([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLocationChange = (e) => {
        const value = e.target.value;
        setLocationInput(value);
        setLocation(null);
        
        if (value.length >= 1 && destinations) {
            const regex = new RegExp(`${value}`, "gi");
            const matches = destinations.filter((item) =>
                regex.test(item.city) ||
                regex.test(item.country) ||
                regex.test(item.state)
            );
            setSuggestions(matches.slice(0, 6));
        } else {
            setSuggestions([]);
        }
    };

    const selectLocation = (item) => {
        setLocation(item);
        setLocationInput(`${item.city}${item.state ? `, ${item.state}` : ''}, ${item.country}`);
        setSuggestions([]);
        setActiveField('date');
    };

    const buildSearchUrl = () => {
        if (!location) return null;
        
        const linkCity = location.city.replace(/\s+/g, '-');
        const linkState = location.state ? location.state.replace(/\s+/g, '-') : '';
        const linkCountry = location.country.replace(/\s+/g, '-');
        
        let path = linkState 
            ? `${linkCity}--${linkState}--${linkCountry}`
            : `${linkCity}--${linkCountry}`;
        
        const params = new URLSearchParams();
        if (date) params.append('date', date);
        if (guests > 1) params.append('guests', guests);
        
        return `/s/${path}${params.toString() ? `?${params.toString()}` : ''}`;
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const url = buildSearchUrl();
        if (url) {
            navigate(url);
        } else if (suggestions.length === 1) {
            selectLocation(suggestions[0]);
        }
    };

    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    if (jumbo) {
        return (
            <div className="hero-search-section">
                <div className="hero-background">
                    <div className="hero-overlay"></div>
                    <div className="hero-content">
                        <h1 className="hero-title">{t('home-hero-title')}</h1>
                        <p className="hero-subtitle">{t('home-hero-subtitle')}</p>
                        
                        <div className="search-bar-container" ref={searchRef}>
                            <form onSubmit={handleSearch} className="search-bar">
                                {/* Location Field */}
                                <div 
                                    className={`search-field location-field ${activeField === 'location' ? 'active' : ''}`}
                                    onClick={() => setActiveField('location')}
                                >
                                    <label>Where</label>
                                    <input
                                        type="text"
                                        placeholder="Search destinations"
                                        value={locationInput}
                                        onChange={handleLocationChange}
                                        onFocus={() => setActiveField('location')}
                                    />
                                    {location && <i className="bi bi-check-circle-fill text-success field-check"></i>}
                                    
                                    {/* Location Suggestions Dropdown */}
                                    {activeField === 'location' && suggestions.length > 0 && (
                                        <div className="search-dropdown">
                                            {suggestions.map(item => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    className="dropdown-item"
                                                    onClick={() => selectLocation(item)}
                                                >
                                                    <i className="bi bi-geo-alt"></i>
                                                    <span>
                                                        {item.city}{item.state && `, ${item.state}`}, {item.country}
                                                    </span>
                                                    <Emoji label={item.country} emoji={item.flag} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="search-divider"></div>

                                {/* Date Field */}
                                <div 
                                    className={`search-field date-field ${activeField === 'date' ? 'active' : ''}`}
                                    onClick={() => setActiveField('date')}
                                >
                                    <label>When</label>
                                    <input
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        onFocus={() => setActiveField('date')}
                                        min={getMinDate()}
                                        placeholder="Add dates"
                                    />
                                </div>

                                <div className="search-divider"></div>

                                {/* Guests Field */}
                                <div 
                                    className={`search-field guests-field ${activeField === 'guests' ? 'active' : ''}`}
                                    onClick={() => setActiveField('guests')}
                                >
                                    <label>Who</label>
                                    <div className="guests-display">
                                        {guests} {guests === 1 ? 'guest' : 'guests'}
                                    </div>
                                    
                                    {activeField === 'guests' && (
                                        <div className="search-dropdown guests-dropdown">
                                            <div className="guests-selector">
                                                <span>Guests</span>
                                                <div className="guests-controls">
                                                    <button
                                                        type="button"
                                                        className="guest-btn"
                                                        onClick={(e) => { e.stopPropagation(); setGuests(Math.max(1, guests - 1)); }}
                                                        disabled={guests <= 1}
                                                    >
                                                        <i className="bi bi-dash"></i>
                                                    </button>
                                                    <span className="guest-count">{guests}</span>
                                                    <button
                                                        type="button"
                                                        className="guest-btn"
                                                        onClick={(e) => { e.stopPropagation(); setGuests(Math.min(20, guests + 1)); }}
                                                        disabled={guests >= 20}
                                                    >
                                                        <i className="bi bi-plus"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Search Button */}
                                <button type="submit" className="search-button" disabled={!location}>
                                    <i className="bi bi-search"></i>
                                    <span>Search</span>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Compact version for other pages
    return (
        <div className="search-bar-compact" ref={searchRef}>
            <form onSubmit={handleSearch} className="search-bar">
                <div 
                    className={`search-field location-field ${activeField === 'location' ? 'active' : ''}`}
                    onClick={() => setActiveField('location')}
                >
                    <i className="bi bi-geo-alt"></i>
                    <input
                        type="text"
                        placeholder={t('search-placeholder')}
                        value={locationInput}
                        onChange={handleLocationChange}
                        onFocus={() => setActiveField('location')}
                    />
                    
                    {activeField === 'location' && suggestions.length > 0 && (
                        <div className="search-dropdown">
                            {suggestions.map(item => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="dropdown-item"
                                    onClick={() => selectLocation(item)}
                                >
                                    <i className="bi bi-geo-alt"></i>
                                    <span>
                                        {item.city}{item.state && `, ${item.state}`}, {item.country}
                                    </span>
                                    <Emoji label={item.country} emoji={item.flag} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <button type="submit" className="search-button-compact">
                    <i className="bi bi-search"></i>
                </button>
            </form>
        </div>
    );
}

export default Search;
